import { supabase } from '../../../core/lib/supabase';
import type { KnowledgeBaseFolder } from '../../../types/navigationTypes';
import { getBackendAuthHeaders } from '../../../shared/services/backendAuthService';

const UPLOAD_BACKEND_BASE_URL = process.env.VITE_UPLOAD_BACKEND_URL || '';

function buildUrl(baseUrl: string, path: string): string {
  const normalizedBase = baseUrl.replace(/\/+$/, '');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${normalizedBase}${normalizedPath}`;
}

export interface UploadDocumentParams {
  file: File;
  knowledgeBaseId: string;
  folderId: string | null;
  folderName?: string;
  folderDescription?: string;
  documentName: string;
  description?: string;
}

export interface UploadDocumentResult {
  success: boolean;
  documentId?: string;
  error?: string;
}

/**
 * Obtiene las carpetas (folders) de una knowledge base
 * En el nuevo modelo, las carpetas son nodos de tipo 'folder'
 */
export async function getFoldersForKnowledgeBase(
  knowledgeBaseId: string
): Promise<KnowledgeBaseFolder[]> {
  console.log('[getFoldersForKnowledgeBase] Iniciando carga de carpetas:', {
    knowledgeBaseId,
    timestamp: new Date().toISOString()
  });

  try {
    const { data, error } = await supabase
      .from('nodes')
      .select('id, name, knowledge_base_id, parent_id, created_at, created_by')
      .eq('knowledge_base_id', knowledgeBaseId)
      .eq('type', 'folder')
      .order('name', { ascending: true });

    console.log('[getFoldersForKnowledgeBase] Respuesta de Supabase:', {
      foldersCount: data?.length || 0,
      folders: data,
      error: error ? {
        message: error.message,
        code: error.code,
        details: error.details
      } : null,
      timestamp: new Date().toISOString()
    });

    if (error) {
      console.error('[getFoldersForKnowledgeBase] Error fetching folders:', error);
      throw error;
    }

    const result: KnowledgeBaseFolder[] = (data || []).map(folder => ({
      id: folder.id,
      name: folder.name,
      knowledge_base_id: folder.knowledge_base_id,
      parent_id: folder.parent_id,
      type: 'folder' as const,
      created_at: folder.created_at,
      created_by: folder.created_by
    }));

    console.log('[getFoldersForKnowledgeBase] Carpetas obtenidas exitosamente:', {
      count: result.length,
      folders: result.map(f => ({ id: f.id, name: f.name })),
      timestamp: new Date().toISOString()
    });

    return result;
  } catch (error) {
    console.error('[getFoldersForKnowledgeBase] Error capturado:', {
      error,
      errorMessage: error instanceof Error ? error.message : 'Error desconocido',
      knowledgeBaseId,
      timestamp: new Date().toISOString()
    });
    return [];
  }
}

/**
 * @deprecated Use getFoldersForKnowledgeBase instead
 */
export async function getCategoriesForKnowledgeBase(
  knowledgeBaseId: string
): Promise<KnowledgeBaseFolder[]> {
  console.warn('[getCategoriesForKnowledgeBase] Deprecated, using getFoldersForKnowledgeBase');
  return getFoldersForKnowledgeBase(knowledgeBaseId);
}

/**
 * Crea una nueva carpeta (folder) en una knowledge base
 * En el nuevo modelo, las carpetas son nodos de tipo 'folder'
 */
export async function createFolder(
  knowledgeBaseId: string,
  name: string,
  description?: string
): Promise<KnowledgeBaseFolder> {
  console.log('[createFolder] Iniciando creación de carpeta:', {
    knowledgeBaseId,
    name,
    description: description || null,
    timestamp: new Date().toISOString()
  });

  try {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    console.log('[createFolder] Estado de autenticación:', {
      user: user ? { id: user.id, email: user.email } : null,
      authError: authError ? { message: authError.message, status: authError.status } : null,
      timestamp: new Date().toISOString()
    });

    if (authError || !user) {
      const errorMsg = 'User not authenticated';
      console.error('[createFolder] Auth error:', errorMsg);
      throw new Error(errorMsg);
    }

    const insertData = {
      name,
      knowledge_base_id: knowledgeBaseId,
      type: 'folder',
      parent_id: null,
      created_by: user.id
    };

    console.log('[createFolder] Data to insert:', {
      insertData,
      timestamp: new Date().toISOString()
    });

    const { data, error } = await supabase
      .from('nodes')
      .insert(insertData)
      .select()
      .single();

    console.log('[createFolder] Supabase response:', {
      data,
      error: error ? {
        message: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint
      } : null,
      timestamp: new Date().toISOString()
    });

    if (error) {
      console.error('[createFolder] Error creating folder in Supabase:', {
        error,
        errorMessage: error.message,
        errorCode: error.code,
        errorDetails: error.details,
        errorHint: error.hint,
        insertData,
        timestamp: new Date().toISOString()
      });
      throw error;
    }

    if (!data) {
      const errorMsg = 'No data received after inserting the folder';
      console.error('[createFolder] Error:', errorMsg);
      throw new Error(errorMsg);
    }

    console.log('[createFolder] Folder created successfully:', {
      folder: data,
      timestamp: new Date().toISOString()
    });

    return {
      id: data.id,
      name: data.name,
      knowledge_base_id: data.knowledge_base_id,
      parent_id: data.parent_id,
      type: 'folder',
      created_at: data.created_at,
      created_by: data.created_by
    };
  } catch (error) {
    console.error('[createFolder] Error capturado:', {
      error,
      errorType: typeof error,
      errorMessage: error instanceof Error ? error.message : 'Error desconocido',
      errorStack: error instanceof Error ? error.stack : undefined,
      knowledgeBaseId,
      name,
      timestamp: new Date().toISOString()
    });
    throw error;
  }
}

/**
 * @deprecated Use createFolder instead
 */
export async function createCategory(
  knowledgeBaseId: string,
  name: string,
  description?: string
): Promise<KnowledgeBaseFolder> {
  console.warn('[createCategory] Deprecated, using createFolder');
  return createFolder(knowledgeBaseId, name, description);
}

/**
 * Obtiene o crea una carpeta
 */
export async function getOrCreateFolder(
  knowledgeBaseId: string,
  folderId: string | null,
  folderName?: string,
  folderDescription?: string
): Promise<KnowledgeBaseFolder> {
  if (folderId) {
    const { data, error } = await supabase
      .from('nodes')
      .select('id, name, knowledge_base_id, parent_id, created_at, created_by')
      .eq('id', folderId)
      .eq('type', 'folder')
      .single();

    if (error) {
      console.error('Error fetching folder:', error);
      throw error;
    }

    return {
      id: data.id,
      name: data.name,
      knowledge_base_id: data.knowledge_base_id,
      parent_id: data.parent_id,
      type: 'folder',
      created_at: data.created_at,
      created_by: data.created_by
    };
  }

  if (folderName) {
    return await createFolder(knowledgeBaseId, folderName, folderDescription);
  }

  throw new Error('Either folderId or folderName must be provided');
}

/**
 * @deprecated Use getOrCreateFolder instead
 */
export async function getOrCreateCategory(
  knowledgeBaseId: string,
  categoryId: string | null,
  categoryName?: string,
  categoryDescription?: string
): Promise<KnowledgeBaseFolder> {
  console.warn('[getOrCreateCategory] Deprecated, using getOrCreateFolder');
  return getOrCreateFolder(knowledgeBaseId, categoryId, categoryName, categoryDescription);
}

/**
 * Obtiene la extensión del archivo
 */
function getFileExtension(fileName: string): string {
  const parts = fileName.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
}

/**
 * Determina el MIME type basado en la extensión del archivo
 */
function getMimeType(fileName: string): string {
  const ext = getFileExtension(fileName);
  const mimeTypes: Record<string, string> = {
    'pdf': 'application/pdf',
    'doc': 'application/msword',
    'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'xls': 'application/vnd.ms-excel',
    'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'ppt': 'application/vnd.ms-powerpoint',
    'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'txt': 'text/plain',
    'csv': 'text/csv',
    'json': 'application/json',
    'xml': 'application/xml',
    'png': 'image/png',
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'gif': 'image/gif',
    'zip': 'application/zip'
  };
  return mimeTypes[ext] || 'application/octet-stream';
}

/**
 * Sube un documento a una knowledge base
 * En el nuevo modelo, los documentos son nodos de tipo 'file'
 */
export async function uploadDocumentToKnowledgeBase(
  params: UploadDocumentParams
): Promise<UploadDocumentResult> {
  try {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      return {
        success: false,
        error: 'User not authenticated'
      };
    }

    // Carpeta contenedora, o raíz de la KB (parent_id null) — mismo contrato que el chat web
    let parentIdForFile: string | null;
    const wantsKbRoot =
      params.folderId === null && !(params.folderName && params.folderName.trim());

    if (wantsKbRoot) {
      parentIdForFile = null;
    } else {
      try {
        const folder = await getOrCreateFolder(
          params.knowledgeBaseId,
          params.folderId,
          params.folderName,
          params.folderDescription
        );
        parentIdForFile = folder.id;
      } catch (error) {
        return {
          success: false,
          error: `Error getting/creating folder: ${error instanceof Error ? error.message : 'Unknown error'}`
        };
      }
    }

    const fileExtension = getFileExtension(params.file.name);
    const mimeType = getMimeType(params.file.name);

    // Crear el nodo de tipo 'file' en la base de datos
    const { data: fileNode, error: dbError } = await supabase
      .from('nodes')
      .insert({
        name: params.documentName,
        knowledge_base_id: params.knowledgeBaseId,
        type: 'file',
        parent_id: parentIdForFile,
        status: 'pending',
        created_by: user.id,
        mime_type: mimeType,
        size: params.file.size
      })
      .select()
      .single();

    if (dbError) {
      console.error('Error creating file node:', dbError);
      return {
        success: false,
        error: `Error creating file node: ${dbError.message}`
      };
    }

    // Generar path de storage: {nodeId}/document.{ext}
    const storagePath = `${fileNode.id}/document.${fileExtension}`;

    // Subir archivo a Storage
    const { error: storageError } = await supabase.storage
      .from('documents')
      .upload(storagePath, params.file, {
        contentType: mimeType,
        upsert: false
      });

    if (storageError) {
      // Marcar nodo como failed
      await supabase
        .from('nodes')
        .update({ status: 'failed' })
        .eq('id', fileNode.id);

      // Intentar eliminar el archivo si se subió parcialmente
      try {
        await supabase.storage
          .from('documents')
          .remove([storagePath]);
      } catch (cleanupError) {
        console.error('Error cleaning up failed upload:', cleanupError);
      }

      return {
        success: false,
        error: `Error uploading file: ${storageError.message}`
      };
    }

    // Actualizar nodo con status 'completed'
    const { error: updateError } = await supabase
      .from('nodes')
      .update({ status: 'completed' })
      .eq('id', fileNode.id);

    if (updateError) {
      console.error('Error updating node status:', updateError);
    }

    // Llamar a API externa para procesamiento (opcional)
    const uploadEndpoint = UPLOAD_BACKEND_BASE_URL
      ? buildUrl(UPLOAD_BACKEND_BASE_URL, '/upload-pdf')
      : '';
    if (uploadEndpoint) {
      try {
        const headers = await getBackendAuthHeaders(true);
        await fetch(uploadEndpoint, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            node_id: fileNode.id,
            storage_path: storagePath
          })
        });
      } catch (apiError) {
        console.warn('Error calling upload endpoint:', apiError);
      }
    }

    return {
      success: true,
      documentId: fileNode.id
    };

  } catch (error) {
    console.error('Error in uploadDocumentToKnowledgeBase:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Error desconocido'
    };
  }
}
